/**
 * Onboarding step renderers.
 *
 * Each step is a stateless component: it receives the relevant slice of state
 * and its setter(s) from the parent (`Onboarding.jsx`). Splitting them out of
 * the parent keeps the orchestrator file small and lets us iterate on
 * individual step UX without sifting through unrelated code.
 *
 * ⚠️ All <Select> dropdowns in this file use `<NativeSelect>` (not Radix UI).
 *    Reason: Radix Select's portal breaks on iOS Safari when there's an
 *    ancestor with `transform`/`filter` — our ambient halos trigger exactly
 *    that. The native select element also gives mobile users the iOS wheel picker
 *    which is better UX for short lists.
 */
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { IconToggle } from "../../components/ui/icon-toggle";
import { NativeSelect } from "../../components/ui/native-select";
import { Plus, Trash2, LineChart, CreditCard, Target, Wallet, Banknote, Sparkles, X } from "lucide-react";
import { useEffect, useMemo } from "react";
import { useZodForm } from "../../components/form-ux";
import { Step1ProfileSchema } from "../../lib/onboardingSchemas";
import { tx } from "../../i18n";

const CURRENCIES = [
  { value: "USD", label: "USD — US Dollar" },
  { value: "COP", label: "COP — Peso colombiano" },
  { value: "EUR", label: "EUR — Euro" },
  { value: "MXN", label: "MXN — Peso mexicano" },
  { value: "BRL", label: "BRL — Real brasileño" },
  { value: "ARS", label: "ARS — Peso argentino" },
  { value: "PEN", label: "PEN — Sol peruano" },
  { value: "CLP", label: "CLP — Peso chileno" },
  { value: "GBP", label: "GBP — British Pound" },
];
// Activity options keyed by lang — the labels are user-facing so we localize
// them (the stored value stays in English for backend stability).
const ACTIVITIES_EN = [
  "Freelance Designer", "Freelance Developer", "UX Designer", "Writer",
  "Photographer", "Consultant", "Marketing", "Translator", "Coach", "Other",
];
const ACTIVITIES_ES = {
  "Freelance Designer": "Diseñador/a freelance",
  "Freelance Developer": "Dev freelance",
  "UX Designer": "Diseñador/a UX",
  "Writer": "Escritor/a",
  "Photographer": "Fotógrafo/a",
  "Consultant": "Consultor/a",
  "Marketing": "Marketing",
  "Translator": "Traductor/a",
  "Coach": "Coach",
  "Other": "Otro",
};
const INVESTMENT_KINDS = ["stocks", "crypto", "etf", "bonds", "fund", "dividend", "other"];
const DEBT_KINDS = ["credit_card", "personal_loan", "mortgage", "auto_loan", "revolving_credit", "other"];
const GOAL_KINDS = ["travel", "study", "gift", "emergency_fund", "purchase", "other"];

// Lightweight currency formatter for live-totals (we don't want to import the
// full FX helper just for the onboarding hint).
const fmt = (n, ccy) => {
  if (!Number.isFinite(n) || n === 0) return "0";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency", currency: ccy, maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `${ccy} ${n.toLocaleString()}`;
  }
};

const StepHeading = ({ title, subtitle }) => (
  <div>
    <h2 className="font-serif-display text-3xl font-medium text-slate-900 tracking-tight leading-tight">{title}</h2>
    {subtitle && <p className="text-slate-600 mt-1">{subtitle}</p>}
  </div>
);

// Shared inline error renderer used by all onboarding sub-forms. Lives
// at module scope so React doesn't re-create the component type on every
// parent render (which would tank input focus and lint complains via
// react/no-unstable-nested-components).
const ErrLine = ({ errors, k, testid }) => errors?.[k] ? (
  <p role="alert" data-testid={testid} className="text-[11px] text-rose-600 font-medium mt-1 form-err-msg">{errors[k]}</p>
) : null;
const errCls = (errors, k, base = "h-11") =>
  errors?.[k] ? `${base} border-rose-300 focus-visible:ring-rose-300/40` : base;

const LiveHint = ({ label, value, testid }) => (
  <div
    data-testid={testid}
    className="rounded-2xl border border-emerald-100 bg-emerald-50/60 px-4 py-3 text-sm flex items-center justify-between"
  >
    <span className="text-emerald-900 font-medium flex items-center gap-2">
      <Sparkles className="h-3.5 w-3.5" />
      {label}
    </span>
    <span className="font-mono-num text-emerald-700 font-bold">{value}</span>
  </div>
);

/* ------------------------------------------------------------------ Step 1 */
export function StepProfile({ t, lang, profile, setProfile, forceShowErrors = false }) {
  const activityOptions = useMemo(
    () => ACTIVITIES_EN.map((a) => ({ value: a, label: tx(lang, a, ACTIVITIES_ES[a], ACTIVITIES_ES[a]) })),
    [lang],
  );

  // iter-169zzg — migrated to `useZodForm`. Source of truth still lives
  // in the parent (`profile` / `setProfile`) because other steps read
  // `profile.currency` etc., but the *errors / touched / submit count*
  // state is owned by the hook. This gives us:
  //   • Inline Zod validation per field on blur (touched-gated).
  //   • Auto-telemetry under formId="onb-step1".
  //   • `forceShowErrors` (set by parent when Next is blocked) flips
  //     submitCount via the hook so the inline error surfaces even if
  //     the user never blurred the field — same UX as the legacy
  //     "shake + toast" path but with actionable inline copy.
  //
  // Sync direction: hook.values → parent on every change. The parent
  // remains authoritative for cross-step reads (welcome banner, etc.).
  const isEs = lang === "es";
  const form = useZodForm(Step1ProfileSchema, profile, { formId: "onb-step1" });

  // Mirror local form state up to the parent. Stringify-compare to avoid
  // a feedback loop when the parent re-renders with the same object
  // identity (initialState above creates a NEW profile dict on first
  // mount only, so this is a guard-rail for future regressions).
  useEffect(() => {
    if (JSON.stringify(form.values) !== JSON.stringify(profile)) {
      setProfile(form.values);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values]);

  // When the parent fails its own Next-click safeParse, it flips
  // `forceShowErrors`. We translate that into a synthetic
  // safeParse → setErrors so the inline messages render and the
  // submit-button-error-ring shows on the real culprit field.
  useEffect(() => {
    if (!forceShowErrors) return;
    const result = Step1ProfileSchema.safeParse(form.values);
    if (!result.success) {
      const fe = {};
      for (const issue of result.error.issues || []) {
        const k = issue.path[issue.path.length - 1];
        if (k && !fe[k]) fe[k] = issue.message;
      }
      form.setErrors(fe);
      // Touch ALL fields so getFieldProps surfaces the errors.
      Object.keys(form.values).forEach((k) => form.markTouched(k));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceShowErrors]);

  const nameProps = form.getFieldProps("full_name");
  const incomeProps = form.getFieldProps("avg_monthly_income");

  return (
    <div className="mt-7 space-y-4">
      <StepHeading title={t("onb_welcome_title")} subtitle={t("onb_welcome_sub")} />
      <div>
        <Label htmlFor="onb-full-name-input" className="text-xs font-bold uppercase tracking-wider text-slate-600 flex items-center gap-1">
          {t("full_name")}
          <span className="text-rose-500" aria-hidden="true">*</span>
        </Label>
        <Input
          id="onb-full-name-input"
          data-testid="onb-full-name"
          name="full_name"
          value={nameProps.value}
          onChange={nameProps.onChange}
          onBlur={nameProps.onBlur}
          placeholder={isEs ? "ej. Laura Gómez" : "e.g. Laura Gómez"}
          autoComplete="name"
          aria-required="true"
          aria-invalid={nameProps.error ? "true" : undefined}
          aria-describedby={nameProps.error ? "onb-full-name-error" : "onb-full-name-hint"}
          className={`mt-1.5 h-11 ${nameProps.error ? "border-rose-300 focus-visible:ring-rose-300/40" : ""}`.trim()}
        />
        {nameProps.error ? (
          <p
            id="onb-full-name-error"
            role="alert"
            data-testid="onb-full-name-error"
            className="text-[11px] text-rose-600 font-medium mt-1 form-err-msg"
          >
            {isEs ? "Ingresa tu nombre para continuar." : "Enter your name to continue."}
          </p>
        ) : (
          <p id="onb-full-name-hint" className="text-[11px] text-slate-500 mt-1">
            {isEs
              ? "Usamos tu nombre en saludos del dashboard y emails."
              : "We use your name in dashboard greetings and emails."}
          </p>
        )}
      </div>
      <div>
        <Label htmlFor="onb-activity" className="text-xs font-bold uppercase tracking-wider text-slate-600">{t("professional_activity")}</Label>
        <NativeSelect
          id="onb-activity"
          testid="onb-activity"
          value={form.values.professional_activity}
          onValueChange={(v) => form.setValue("professional_activity", v)}
          options={activityOptions}
          className="mt-1.5 h-11"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="onb-currency" className="text-xs font-bold uppercase tracking-wider text-slate-600">{t("currency")}</Label>
          <NativeSelect
            id="onb-currency"
            testid="onb-currency"
            value={form.values.currency}
            onValueChange={(v) => form.setValue("currency", v)}
            options={CURRENCIES}
            className="mt-1.5 h-11"
          />
        </div>
        <div>
          <Label htmlFor="onb-income" className="text-xs font-bold uppercase tracking-wider text-slate-600">{t("avg_monthly_income")}</Label>
          <Input
            id="onb-income"
            data-testid="onb-income"
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={incomeProps.value}
            onChange={(e) => {
              // Reject non-numeric input client-side so users on
              // mobile can't accidentally type a stray "k" or comma.
              const v = e.target.value;
              if (v === "" || /^[0-9]+(?:[.,][0-9]*)?$/.test(v)) {
                form.setValue("avg_monthly_income", v);
              }
            }}
            onBlur={incomeProps.onBlur}
            placeholder={isEs ? "ej. 6000000" : "e.g. 6000000"}
            className="mt-1.5 h-11"
          />
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ Step 2 */
const ACCOUNT_ICONS = {
  cash: Banknote,
  bank: CreditCard,  // CreditCard double-duty for bank visuals; matches Cuentas icon set
  wallet: Wallet,
};

export function StepMoney({ t, lang, accounts, setAccounts, currency }) {
  // Sum across visible accounts for the live hint. We only count rows that
  // are currently shown (visible) AND have a balance > 0.
  const total = accounts.reduce(
    (s, a) => (a.visible !== false ? s + (Number(a.balance) || 0) : s),
    0,
  );

  const toggleVisibility = (type, visible) => {
    setAccounts((arr) => arr.map((a) => (a.type === type ? { ...a, visible, balance: visible ? a.balance : "" } : a)));
  };

  const visibleAccounts = accounts.filter((a) => a.visible !== false);
  const hiddenAccounts = accounts.filter((a) => a.visible === false);

  return (
    <div className="mt-7 space-y-4">
      <StepHeading title={t("money_title")} subtitle={t("money_sub")} />

      {visibleAccounts.map((a) => {
        const Icon = ACCOUNT_ICONS[a.type] || Banknote;
        // Bank is mandatory (we want at least one liquid account for runway
        // math). Cash/Wallet are optional and can be hidden via the X icon.
        const canHide = a.type !== "bank";
        return (
          <div key={a.type} className="grid grid-cols-[36px_1fr_120px] gap-3 items-end">
            <div className="h-11 w-9 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center self-end">
              <Icon className="h-4 w-4" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 flex items-center justify-between">
                <span className="flex items-center gap-1">
                  {a.name}
                  {!canHide && (
                    <span className="text-rose-500" aria-hidden="true">*</span>
                  )}
                </span>
                {canHide && (
                  <button /* design-system: ALLOW legacy-todo */
                    type="button"
                    data-testid={`onb-account-${a.type}-hide`}
                    onClick={() => toggleVisibility(a.type, false)}
                    className="text-[10px] normal-case font-medium text-slate-400 hover:text-red-600 inline-flex items-center gap-1"
                  >
                    <X className="h-3 w-3" /> {tx(lang, "hide", "ocultar", "ocultar")}
                  </button>
                )}
              </Label>
              <Input
                data-testid={`onb-account-${a.type}`}
                type="text"
                inputMode="decimal"
                autoComplete="off"
                value={a.balance}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "" || /^[0-9]+(?:[.,][0-9]*)?$/.test(v)) {
                    setAccounts((arr) => arr.map((x) => (x.type === a.type ? { ...x, balance: v } : x)));
                  }
                }}
                placeholder="0"
                className="mt-1.5 h-11"
              />
            </div>
            <div className="text-xs font-semibold uppercase text-slate-500 pb-3">{a.type}</div>
          </div>
        );
      })}

      {hiddenAccounts.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-1">
          {hiddenAccounts.map((a) => (
            <Button
              key={a.type}
              type="button"
              variant="outline"
              size="sm"
              data-testid={`onb-account-${a.type}-show`}
              onClick={() => toggleVisibility(a.type, true)}
              className="gap-1.5 text-slate-600 border-dashed"
            >
              <Plus className="h-3.5 w-3.5" />
              {tx(lang, "Add", "Agregar", "Agregar")} {a.name}
            </Button>
          ))}
        </div>
      )}

      <LiveHint
        testid="onb-money-total"
        label={tx(lang, "Total liquidity", "Caja total", "Caja total")}
        value={fmt(total, currency)}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ Step 3 */
export function StepReceivables({ t, lang, currency, recv, setRecv, receivables, addReceivable, removeReceivable, errors = {}, setErrors }) {
  const total = receivables.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const probabilityOptions = [
    { value: "high", label: t("high") },
    { value: "medium", label: t("medium") },
    { value: "low", label: t("low") },
  ];
  // Clear a field's error as soon as the user types into it. Keeps the
  // form punitive only until the user starts engaging.
  const update = (key, value) => {
    setRecv({ ...recv, [key]: value });
    if (errors[key] && setErrors) setErrors({ ...errors, [key]: undefined });
  };
  return (
    <div className="mt-7 space-y-4">
      <StepHeading title={t("receivables_title")} subtitle={t("receivables_sub")} />
      <p className="text-xs text-slate-500 -mt-2">
        {tx(lang, "Add invoices/income you expect to collect soon. Skip if you have none.", "Agrega los cobros que esperas cobrar pronto. Si no tienes ninguno, puedes saltar este paso.", "Agrega los cobros que esperas cobrar pronto. Si no tienes ninguno, puedes saltar este paso.")}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Input data-testid="onb-recv-client" placeholder={`${t("client_name")} *`} value={recv.client_name} onChange={(e) => update("client_name", e.target.value)} aria-invalid={errors.client_name ? "true" : undefined} className={errCls(errors, "client_name")} />
          <ErrLine errors={errors} k="client_name" testid="onb-recv-client-err" />
        </div>
        <div>
          <Input data-testid="onb-recv-amount" type="text" inputMode="decimal" autoComplete="off" placeholder={`${t("amount")} *`} value={recv.amount} onChange={(e) => update("amount", e.target.value)} aria-invalid={errors.amount ? "true" : undefined} className={errCls(errors, "amount")} />
          <ErrLine errors={errors} k="amount" testid="onb-recv-amount-err" />
        </div>
        <div>
          <Input data-testid="onb-recv-date" type="date" value={recv.expected_date} onChange={(e) => update("expected_date", e.target.value)} aria-invalid={errors.expected_date ? "true" : undefined} className={errCls(errors, "expected_date")} />
          <ErrLine errors={errors} k="expected_date" testid="onb-recv-date-err" />
        </div>
        <NativeSelect
          testid="onb-recv-prob"
          value={recv.probability}
          onValueChange={(v) => setRecv({ ...recv, probability: v })}
          options={probabilityOptions}
          className="h-11"
        />
      </div>
      <Button data-testid="onb-add-recv-btn" onClick={addReceivable} variant="outline" className="gap-2"><Plus className="h-4 w-4" />{t("new_receivable")}</Button>
      {receivables.length > 0 && (
        <ul className="divide-y divide-slate-100 border border-slate-200 rounded-xl">
          {receivables.map((r) => (
            <li key={r.id} className="flex items-center justify-between px-3 py-2 text-sm">
              <span>{r.client_name} • <span className="tabular-nums">{fmt(Number(r.amount), currency)}</span> • {r.probability}</span>
              <IconToggle onClick={() => removeReceivable(r.id)} variant="danger" size="sm" aria-label="Remove"><Trash2 className="h-4 w-4" /></IconToggle>
            </li>
          ))}
        </ul>
      )}
      {receivables.length > 0 && (
        <LiveHint
          testid="onb-recv-total"
          label={tx(lang, `${receivables.length} expected receivable${receivables.length === 1 ? "" : "s"}`, `${receivables.length} cobro${receivables.length === 1 ? "" : "s"} esperado${receivables.length === 1 ? "" : "s"}`, `${receivables.length} cobro${receivables.length === 1 ? "" : "s"} esperado${receivables.length === 1 ? "" : "s"}`)}
          value={fmt(total, currency)}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ Step 4 */
export function StepPayments({ t, lang, currency, pay, setPay, payments, addPayment, removePayment, errors = {}, setErrors }) {
  const total = payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const typeOptions = [
    { value: "work", label: tx(lang, "Work", "Trabajo", "Trabajo") },
    { value: "personal", label: tx(lang, "Personal", "Personal", "Personal") },
    { value: "debt", label: tx(lang, "Debt", "Deuda", "Deuda") },
    { value: "recurring", label: tx(lang, "Recurring", "Recurrente", "Recurrente") },
  ];
  const update = (key, value) => {
    setPay({ ...pay, [key]: value });
    if (errors[key] && setErrors) setErrors({ ...errors, [key]: undefined });
  };
  return (
    <div className="mt-7 space-y-4">
      <StepHeading title={t("payments_title")} subtitle={t("payments_sub")} />
      <p className="text-xs text-slate-500 -mt-2">
        {tx(lang, "Your upcoming bills: rent, subscriptions, installments. Skip if you have none.", "Tus próximos pagos importantes: arriendos, facturas, cuotas. Puedes saltar si no tienes ninguno.", "Tus próximos pagos importantes: arriendos, facturas, cuotas. Puedes saltar si no tienes ninguno.")}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Input data-testid="onb-pay-concept" placeholder={`${t("concept")} *`} value={pay.concept} onChange={(e) => update("concept", e.target.value)} aria-invalid={errors.concept ? "true" : undefined} className={errCls(errors, "concept")} />
          <ErrLine errors={errors} k="concept" testid="onb-pay-concept-err" />
        </div>
        <div>
          <Input data-testid="onb-pay-amount" type="text" inputMode="decimal" autoComplete="off" placeholder={`${t("amount")} *`} value={pay.amount} onChange={(e) => update("amount", e.target.value)} aria-invalid={errors.amount ? "true" : undefined} className={errCls(errors, "amount")} />
          <ErrLine errors={errors} k="amount" testid="onb-pay-amount-err" />
        </div>
        <div>
          <Input data-testid="onb-pay-date" type="date" value={pay.due_date} onChange={(e) => update("due_date", e.target.value)} aria-invalid={errors.due_date ? "true" : undefined} className={errCls(errors, "due_date")} />
          <ErrLine errors={errors} k="due_date" testid="onb-pay-date-err" />
        </div>
        <NativeSelect
          testid="onb-pay-type"
          value={pay.type}
          onValueChange={(v) => setPay({ ...pay, type: v })}
          options={typeOptions}
          className="h-11"
        />
      </div>
      <Button data-testid="onb-add-pay-btn" onClick={addPayment} variant="outline" className="gap-2"><Plus className="h-4 w-4" />{t("new_payment")}</Button>
      {payments.length > 0 && (
        <ul className="divide-y divide-slate-100 border border-slate-200 rounded-xl">
          {payments.map((p) => (
            <li key={p.id} className="flex items-center justify-between px-3 py-2 text-sm">
              <span>{p.concept} • <span className="tabular-nums">{fmt(Number(p.amount), currency)}</span> • {p.type}</span>
              <IconToggle onClick={() => removePayment(p.id)} variant="danger" size="sm" aria-label="Remove"><Trash2 className="h-4 w-4" /></IconToggle>
            </li>
          ))}
        </ul>
      )}
      {payments.length > 0 && (
        <LiveHint
          testid="onb-pay-total"
          label={tx(lang, `${payments.length} upcoming payment${payments.length === 1 ? "" : "s"}`, `${payments.length} pago${payments.length === 1 ? "" : "s"} próximo${payments.length === 1 ? "" : "s"}`, `${payments.length} pago${payments.length === 1 ? "" : "s"} próximo${payments.length === 1 ? "" : "s"}`)}
          value={fmt(total, currency)}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ Step 5 */
export function StepWithdrawal({ t, lang, currency, withdrawal, setWithdrawal, errors = {}, setErrors }) {
  const update = (key, value) => {
    setWithdrawal({ ...withdrawal, [key]: value });
    if (errors[key] && setErrors) setErrors({ ...errors, [key]: undefined });
  };
  const amountErr = errors.amount;
  return (
    <div className="mt-7 space-y-4">
      <StepHeading title={t("withdrawal_title")} subtitle={t("withdrawal_sub")} />
      <p className="text-xs text-slate-500 -mt-2">
        {tx(lang, "Think of how much you want to transfer each month to your personal account, without dipping into business operations. Lumfi alerts you if you're over-withdrawing.", "Piensa en cuánto quieres transferir cada mes a tu cuenta personal para vivir, sin tocar la operación del negocio. Lumfi te avisa si estás retirando de más.", "Piensa en cuánto quieres transferir cada mes a tu cuenta personal para vivir, sin tocar la operación del negocio. Lumfi te avisa si estás retirando de más.")}
      </p>
      <div>
        <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 flex items-center gap-1">
          {t("desired_amount")}
          <span className="text-rose-500" aria-hidden="true">*</span>
        </Label>
        <div className="mt-1.5 flex items-center gap-2">
          <span className="font-mono-num text-sm text-slate-500 px-2 py-2 rounded-md bg-slate-100 border border-slate-200" data-testid="onb-withdrawal-currency">
            {currency}
          </span>
          <Input
            data-testid="onb-withdrawal-amount"
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={withdrawal.amount}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "" || /^[0-9]+(?:[.,][0-9]*)?$/.test(v)) {
                update("amount", v);
              }
            }}
            placeholder={tx(lang, "e.g. 1000000", "ej. 1000000", "ej. 1000000")}
            aria-invalid={amountErr ? "true" : undefined}
            className={`h-11 flex-1 ${amountErr ? "border-rose-300 focus-visible:ring-rose-300/40" : ""}`.trim()}
          />
        </div>
        {amountErr && (
          <p role="alert" data-testid="onb-withdrawal-amount-err" className="text-[11px] text-rose-600 font-medium mt-1 form-err-msg">{amountErr}</p>
        )}
      </div>
      <div>
        <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">{t("reason")}</Label>
        <Input
          data-testid="onb-withdrawal-reason"
          value={withdrawal.reason}
          onChange={(e) => setWithdrawal({ ...withdrawal, reason: e.target.value })}
          placeholder={tx(lang, "Monthly living expenses", "Gastos personales del mes", "Gastos personales del mes")}
          className="mt-1.5 h-11"
        />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ Step 6 */
export function StepWealth({
  lang,
  inv, setInv, investments, addInvestment, removeInvestment, invErrors = {}, setInvErrors,
  debtForm, setDebtForm, debts, addDebt, removeDebt, debtErrors = {}, setDebtErrors,
  goalForm, setGoalForm, goals, addGoal, removeGoal, goalErrors = {}, setGoalErrors,
}) {
  const invOptions = INVESTMENT_KINDS.map((k) => ({ value: k, label: k }));
  const debtOptions = DEBT_KINDS.map((k) => ({ value: k, label: k.replace("_", " ") }));
  const goalOptions = GOAL_KINDS.map((k) => ({ value: k, label: k.replace("_", " ") }));
  // Shared helper — clear a field error as soon as the user types.
  const onTyped = (errors, setErrors, key) => () => {
    if (errors[key] && setErrors) setErrors({ ...errors, [key]: undefined });
  };
  // ``errCls`` / ``ErrLine`` are imported from the module-scope helpers
  // at the top of this file. We default ``base`` to "h-10" by calling
  // them with the right base where appropriate.
  const errCls10 = (errors, k) => errCls(errors, k, "h-10");
  return (
    <div className="mt-7 space-y-7">
      <StepHeading
        title={tx(lang, "Wealth (optional)", "Patrimonio (opcional)", "Patrimonio (opcional)")}
        subtitle={tx(lang, "Tell us about your investments, debts and goals to enrich your dashboard. You can skip this step.", "Cuéntanos sobre tus inversiones, deudas y metas para enriquecer tu dashboard. Puedes saltar este paso.", "Cuéntanos sobre tus inversiones, deudas y metas para enriquecer tu dashboard. Puedes saltar este paso.")}
      />

      <WealthSection
        testid="onb-investments-section"
        icon={<LineChart className="h-4 w-4 text-emerald-600" />}
        title={tx(lang, "Investments", "Inversiones", "Inversiones")}
      >
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <div>
            <Input data-testid="onb-inv-name" placeholder={tx(lang, "Name (Apple, BTC...) *", "Nombre (Apple, BTC...) *", "Nombre (Apple, BTC...) *")}
              value={inv.name}
              onChange={(e) => { setInv({ ...inv, name: e.target.value }); onTyped(invErrors, setInvErrors, "name")(); }}
              aria-invalid={invErrors.name ? "true" : undefined}
              className={errCls10(invErrors, "name")} />
            <ErrLine errors={invErrors} k="name" testid="onb-inv-name-err" />
          </div>
          <NativeSelect
            testid="onb-inv-kind"
            value={inv.kind}
            onValueChange={(v) => setInv({ ...inv, kind: v })}
            options={invOptions}
            className="h-10"
          />
          <div>
            <Input data-testid="onb-inv-amount" type="text" inputMode="decimal" autoComplete="off" placeholder={tx(lang, "Amount invested *", "Monto invertido *", "Monto invertido *")}
              value={inv.amount_invested}
              onChange={(e) => { setInv({ ...inv, amount_invested: e.target.value }); onTyped(invErrors, setInvErrors, "amount_invested")(); }}
              aria-invalid={invErrors.amount_invested ? "true" : undefined}
              className={errCls10(invErrors, "amount_invested")} />
            <ErrLine errors={invErrors} k="amount_invested" testid="onb-inv-amount-err" />
          </div>
        </div>
        <Button data-testid="onb-add-investment-btn" onClick={addInvestment} variant="outline" size="sm" className="mt-2 gap-1.5">
          <Plus className="h-4 w-4" />{tx(lang, "Add investment", "Agregar inversión", "Agregar inversión")}
        </Button>
        <WealthList items={investments} keyField="amount_invested" onRemove={removeInvestment} />
      </WealthSection>

      <WealthSection
        testid="onb-debts-section"
        icon={<CreditCard className="h-4 w-4 text-red-600" />}
        title={tx(lang, "Debts", "Deudas", "Deudas")}
      >
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <div>
            <Input data-testid="onb-debt-name" placeholder={tx(lang, "Name *", "Nombre *", "Nombre *")}
              value={debtForm.name}
              onChange={(e) => { setDebtForm({ ...debtForm, name: e.target.value }); onTyped(debtErrors, setDebtErrors, "name")(); }}
              aria-invalid={debtErrors.name ? "true" : undefined}
              className={errCls10(debtErrors, "name")} />
            <ErrLine errors={debtErrors} k="name" testid="onb-debt-name-err" />
          </div>
          <NativeSelect
            testid="onb-debt-kind"
            value={debtForm.kind}
            onValueChange={(v) => setDebtForm({ ...debtForm, kind: v })}
            options={debtOptions}
            className="h-10"
          />
          <div>
            <Input data-testid="onb-debt-balance" type="text" inputMode="decimal" autoComplete="off" placeholder={tx(lang, "Balance *", "Saldo actual *", "Saldo actual *")}
              value={debtForm.balance}
              onChange={(e) => { setDebtForm({ ...debtForm, balance: e.target.value }); onTyped(debtErrors, setDebtErrors, "balance")(); }}
              aria-invalid={debtErrors.balance ? "true" : undefined}
              className={errCls10(debtErrors, "balance")} />
            <ErrLine errors={debtErrors} k="balance" testid="onb-debt-balance-err" />
          </div>
        </div>
        <Button data-testid="onb-add-debt-btn" onClick={addDebt} variant="outline" size="sm" className="mt-2 gap-1.5">
          <Plus className="h-4 w-4" />{tx(lang, "Add debt", "Agregar deuda", "Agregar deuda")}
        </Button>
        <WealthList items={debts} keyField="balance" onRemove={removeDebt} />
      </WealthSection>

      <WealthSection
        testid="onb-goals-section"
        icon={<Target className="h-4 w-4 text-amber-600" />}
        title={tx(lang, "Goals", "Metas", "Metas")}
      >
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <div>
            <Input data-testid="onb-goal-name" placeholder={tx(lang, "Name *", "Nombre *", "Nombre *")}
              value={goalForm.name}
              onChange={(e) => { setGoalForm({ ...goalForm, name: e.target.value }); onTyped(goalErrors, setGoalErrors, "name")(); }}
              aria-invalid={goalErrors.name ? "true" : undefined}
              className={errCls10(goalErrors, "name")} />
            <ErrLine errors={goalErrors} k="name" testid="onb-goal-name-err" />
          </div>
          <NativeSelect
            testid="onb-goal-kind"
            value={goalForm.kind}
            onValueChange={(v) => setGoalForm({ ...goalForm, kind: v })}
            options={goalOptions}
            className="h-10"
          />
          <div>
            <Input data-testid="onb-goal-target" type="text" inputMode="decimal" autoComplete="off" placeholder={tx(lang, "Target amount *", "Monto meta *", "Monto meta *")}
              value={goalForm.target_amount}
              onChange={(e) => { setGoalForm({ ...goalForm, target_amount: e.target.value }); onTyped(goalErrors, setGoalErrors, "target_amount")(); }}
              aria-invalid={goalErrors.target_amount ? "true" : undefined}
              className={errCls10(goalErrors, "target_amount")} />
            <ErrLine errors={goalErrors} k="target_amount" testid="onb-goal-target-err" />
          </div>
        </div>
        <Button data-testid="onb-add-goal-btn" onClick={addGoal} variant="outline" size="sm" className="mt-2 gap-1.5">
          <Plus className="h-4 w-4" />{tx(lang, "Add goal", "Agregar meta", "Agregar meta")}
        </Button>
        <WealthList items={goals} keyField="target_amount" onRemove={removeGoal} />
      </WealthSection>
    </div>
  );
}

function WealthSection({ testid, icon, title, children }) {
  return (
    <section data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <h3 className="font-semibold text-slate-900">{title}</h3>
      </div>
      {children}
    </section>
  );
}

function WealthList({ items, keyField, onRemove }) {
  if (!items.length) return null;
  return (
    <ul className="mt-3 divide-y divide-slate-100 border border-slate-200 rounded-xl">
      {items.map((it) => (
        <li key={it.id} className="flex items-center justify-between px-3 py-2 text-sm">
          <span>{it.name} • <span className="tabular-nums">{it[keyField]}</span> • {it.kind}</span>
          <IconToggle onClick={() => onRemove(it.id)} variant="danger" size="sm" aria-label="Remove"><Trash2 className="h-4 w-4" /></IconToggle>
        </li>
      ))}
    </ul>
  );
}
